import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Printer, Download, ArrowLeft, ShieldCheck } from "lucide-react";
import Layout from "../components/Layout";

/**
 * LegalPage — renders Privacy Policy, Terms of Service, Your Rights as a
 * Customer (YRAC) in a PUCT-style formal document layout matching the EFL
 * page: toolbar with print/PDF, bordered letter-style document, numbered
 * sections, footer with REP info.
 */
export default function LegalPage({ namespace }) {
  const { t } = useTranslation();
  const sections = t(`${namespace}.sections`, { returnObjects: true });
  const updated = t(`${namespace}.updated`);
  const title = t(`${namespace}.title`);
  const docLabel = t(`${namespace}.docLabel`);
  const intro = t(`${namespace}.intro`);
  const navigate = useNavigate();
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/"));
  const [searchParams] = useSearchParams();
  const fromPortal = searchParams.get("from") === "portal";

  return (
    <Layout minimal={fromPortal}>
      <Helmet>
        <title>{title} — Velcor Energy</title>
      </Helmet>

      {/* Toolbar (hidden on print) */}
      <div className="bg-slate-50 border-b border-slate-200 print:hidden">
        <div className="max-w-[820px] mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <button onClick={goBack} type="button" className="inline-flex items-center gap-1.5 text-[13px] font-extrabold text-slate-600 hover:text-velcor-green transition-colors" data-testid="legal-back">
            <ArrowLeft className="w-3.5 h-3.5" />
            {t("eflPage.back")}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-velcor-green hover:text-velcor-green text-[12.5px] font-extrabold transition-colors"
              data-testid="legal-print"
            >
              <Printer className="w-3.5 h-3.5" />
              {t("eflPage.print")}
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-velcor-green text-white text-[12.5px] font-extrabold hover:bg-velcor-green/90"
              data-testid="legal-pdf"
            >
              <Download className="w-3.5 h-3.5" />
              {t("eflPage.savePdf")}
            </button>
          </div>
        </div>
      </div>

      <article
        className="max-w-[820px] mx-auto px-6 py-10 md:py-14 bg-white"
        data-testid={`legal-${namespace}`}
        style={{ fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" }}
      >
        {/* Header */}
        <header className="border-b-2 border-velcor-ink pb-5 mb-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green mb-1">
                {docLabel}
              </div>
              <h1 className="font-black text-[26px] md:text-[32px] text-velcor-ink leading-tight tracking-tight">
                {title}
              </h1>
              <div className="text-slate-500 text-[13px] mt-1.5">{updated}</div>
            </div>
            <img
              src="/velcor-logo.png"
              alt="Velcor Energy"
              width={56}
              height={56}
              style={{ width: 56, height: 56 }}
              className="object-contain shrink-0"
            />
          </div>
        </header>

        {/* Intro */}
        <p className="text-slate-700 text-[14.5px] leading-relaxed mb-8">{intro}</p>

        {/* Sections */}
        <div className="space-y-7">
          {sections.map((s, i) => (
            <section
              key={i}
              className="pb-5 border-b border-slate-100 last:border-b-0"
              data-testid={`legal-section-${i + 1}`}
            >
              <h2 className="font-black text-velcor-ink text-[15px] mb-2.5 tracking-tight flex items-start gap-2.5">
                <span className="inline-flex shrink-0 items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-md bg-velcor-green/10 text-velcor-green text-[11px] font-extrabold tracking-wider">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{s.h}</span>
              </h2>
              <p className="text-slate-700 text-[14px] leading-relaxed pl-[38px] whitespace-pre-line">
                {s.p}
              </p>
            </section>
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-10 pt-6 border-t-2 border-velcor-ink">
          <div className="flex flex-wrap items-center justify-between gap-3 text-[11.5px] text-slate-500">
            <div className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-velcor-green" />
              <span>Velcor Energy LLC · PUCT REP# Pending · 440 Louisiana St, Ste 1200, Houston, TX 77002</span>
            </div>
            <div>© {new Date().getFullYear()} Velcor Energy</div>
          </div>
        </footer>
      </article>
    </Layout>
  );
}
